// What the composer may attach to a Copilot turn, and what it does to a file
// before it leaves the browser (SPEC-COPILOT-02, D-B2).
//
// The same allowlist and the same two limits live in three places — here, in
// the Laravel BFF and in the Agent Runtime (`lib/media.py`) — because each one
// answers a different caller. This layer exists so the clinic learns that a
// 12 MB photo is too big before it spends a minute uploading it, and so a phone
// photo (commonly 4000 px, 6 MB) becomes a 2048 px JPEG that fits.
//
// Media is inline and transient: a prepared file becomes a `data:` URL inside
// the turn that carries it and is never uploaded to any storage route.

import { MEDIA_LIMITS, wholeMegabytes } from "./media-limits";

export type MediaModality = "image" | "pdf" | "audio" | "text";

/** MIME → modality, mirroring `ALLOWED_MEDIA_TYPES` in the Agent Runtime. */
export const ALLOWED_MEDIA_TYPES: Readonly<Record<string, MediaModality>> = {
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "image/heic": "image",
  "image/heif": "image",
  "application/pdf": "pdf",
  "audio/webm": "audio",
  "audio/mp4": "audio",
  "audio/mpeg": "audio",
  "audio/ogg": "audio",
  "audio/wav": "audio",
  "audio/aac": "audio",
  "text/plain": "text",
  "text/csv": "text",
};

/** Decoded bytes, matching the runtime. The base64 envelope adds ~33% on the
 * wire, and the edge caps the request itself at 26 MiB. */
export const MAX_FILE_BYTES = MEDIA_LIMITS.maxFileBytes;
export const MAX_TURN_BYTES = MEDIA_LIMITS.maxTurnBytes;
export const MAX_FILES_PER_TURN = MEDIA_LIMITS.maxAttachments;

/** A phone photo is far larger than any model needs to read a document. */
export const IMAGE_MAX_DIMENSION = 2048;
export const IMAGE_QUALITY = 0.85;
const DOWNSCALE_OUTPUT_TYPE = "image/jpeg";

// HEIC/HEIF pass through untouched: no browser canvas decodes them, and the
// runtime forwards them to a provider that does.
const DOWNSCALABLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const EXTENSION_MEDIA_TYPES: Readonly<Record<string, string>> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  pdf: "application/pdf",
  webm: "audio/webm",
  m4a: "audio/mp4",
  mp3: "audio/mpeg",
  ogg: "audio/ogg",
  wav: "audio/wav",
  aac: "audio/aac",
  txt: "text/plain",
  csv: "text/csv",
};

export const MEDIA_MESSAGES = {
  unsupported: "Esse tipo de arquivo não dá para ler. Use foto, PDF ou áudio.",
  fileTooLarge: `Esse arquivo tem mais de ${wholeMegabytes(MEDIA_LIMITS.maxFileBytes)} MB. Envie uma foto menor.`,
  turnTooLarge: `Envie no máximo ${MEDIA_LIMITS.maxAttachments} arquivos e ${wholeMegabytes(MEDIA_LIMITS.maxTurnBytes)} MB por mensagem.`,
} as const;

export type PreparedFile = {
  file: File;
  mediaType: string;
  size: number;
};

export type MediaPrepResult = {
  /** Files the composer may send, in the order they were attached. */
  files: PreparedFile[];
  /** pt-BR sentences, each naming the limit or the accepted types. */
  errors: string[];
};

export function modalityOf(mediaType: string): MediaModality | null {
  return ALLOWED_MEDIA_TYPES[mediaType.toLowerCase()] ?? null;
}

/** The `accept` attribute for a file input restricted to these modalities. */
export function acceptAttribute(modalities: readonly MediaModality[]): string {
  const allowed = new Set(modalities);
  return Object.entries(ALLOWED_MEDIA_TYPES)
    .filter(([, modality]) => allowed.has(modality))
    .map(([mediaType]) => mediaType)
    .join(",");
}

/** The browser sometimes hands over a file with an empty `type` (HEIC on some
 * Android pickers); the extension is the only other thing we know about it. */
export function mediaTypeOf(file: File): string {
  const declared = (file.type || "").toLowerCase();
  if (declared) return declared;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_MEDIA_TYPES[extension] ?? "";
}

/** pt-BR file size, the way the chip and the persisted stub print it. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

function canDownscale(): boolean {
  return (
    typeof document !== "undefined" &&
    typeof createImageBitmap === "function" &&
    typeof HTMLCanvasElement !== "undefined" &&
    typeof HTMLCanvasElement.prototype.toBlob === "function"
  );
}

function scaledSize(width: number, height: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= IMAGE_MAX_DIMENSION) return { width, height };
  const ratio = IMAGE_MAX_DIMENSION / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * Re-encodes an image as a JPEG no longer than 2048 px on its longest side.
 * Returns the original file when the browser cannot decode it or when the
 * re-encode came out larger — the clinic never gains bytes from this step.
 */
export async function downscaleImage(file: File): Promise<PreparedFile> {
  const original: PreparedFile = { file, mediaType: mediaTypeOf(file), size: file.size };
  if (!canDownscale()) return original;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return original;
  }

  try {
    const { width, height } = scaledSize(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return original;
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, DOWNSCALE_OUTPUT_TYPE, IMAGE_QUALITY);
    });
    if (!blob || blob.size >= file.size) return original;

    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return {
      file: new File([blob], name, { type: DOWNSCALE_OUTPUT_TYPE }),
      mediaType: DOWNSCALE_OUTPUT_TYPE,
      size: blob.size,
    };
  } finally {
    bitmap.close?.();
  }
}

/**
 * Applies the allowlist, the downscale and the two limits, in that order.
 * A refused file never blocks the ones that fit: the turn goes out with what
 * is left and the clinic reads one sentence per distinct reason.
 */
export async function prepareMediaFiles(files: readonly File[]): Promise<MediaPrepResult> {
  const accepted: PreparedFile[] = [];
  const errors: string[] = [];
  let turnBytes = 0;

  for (const file of files) {
    const mediaType = mediaTypeOf(file);
    const modality = modalityOf(mediaType);
    if (!modality) {
      errors.push(MEDIA_MESSAGES.unsupported);
      continue;
    }

    const prepared =
      modality === "image" && DOWNSCALABLE_TYPES.has(mediaType)
        ? await downscaleImage(file)
        : { file, mediaType, size: file.size };

    if (prepared.size > MAX_FILE_BYTES) {
      errors.push(MEDIA_MESSAGES.fileTooLarge);
      continue;
    }

    if (accepted.length >= MAX_FILES_PER_TURN || turnBytes + prepared.size > MAX_TURN_BYTES) {
      errors.push(MEDIA_MESSAGES.turnTooLarge);
      continue;
    }

    turnBytes += prepared.size;
    accepted.push(prepared);
  }

  return { files: accepted, errors: [...new Set(errors)] };
}

/** The `data:` URL an AI SDK file part carries. */
export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("file read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

export type CopilotFilePart = {
  type: "file";
  mediaType: string;
  filename: string;
  url: string;
};

export async function toFileParts(
  prepared: readonly PreparedFile[],
): Promise<CopilotFilePart[]> {
  return Promise.all(
    prepared.map(async (item) => ({
      type: "file" as const,
      mediaType: item.mediaType,
      filename: item.file.name,
      url: await readAsDataUrl(item.file),
    })),
  );
}

/** Decoded byte count of a base64 `data:` URL, for the chip that shows what an
 * already-sent turn carried. The parts in the thread hold no size of their own. */
export function dataUrlByteSize(url: string): number {
  const payload = url.slice(url.indexOf(",") + 1);
  if (!url.includes(",") || payload.length === 0) return 0;
  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
}

/** What the conversation transcript keeps of an attachment (D-B2): never the
 * data URL, only what it was. */
export function attachmentStub(prepared: PreparedFile): string {
  return `[anexo: ${prepared.file.name}, ${prepared.mediaType}, ${formatFileSize(prepared.size)}]`;
}
